import * as checkbox from "@zag-js/checkbox";
import type { Direction } from "@zag-js/types";
import { VanillaMachine, normalizeProps } from "@zag-js/vanilla";

import {
  Component,
  getString,
  getBoolean,
  generateId,
  renderPart,
  getPartIds,
} from "../lib";

export class Checkbox extends Component<checkbox.Props, checkbox.Api> {
  initMachine(props: checkbox.Props): VanillaMachine<any> {
    return new VanillaMachine(checkbox.machine, props);
  }
  initApi(): checkbox.Api {
    return checkbox.connect(this.machine.service, normalizeProps);
  }
  render(): void {
    ["root", "label", "control", "indicator", "hidden-input"].forEach((part) =>
      renderPart(this.el, part, this.api),
    );
  }
}
function parseCheckedState(
  el: HTMLElement,
  attr: "checked" | "defaultChecked",
): checkbox.CheckedState | undefined {
  if (getBoolean(el, attr) === true) return true;
  return getString(el, attr, ["indeterminate"] as const);
}
export function initCheckbox(
  doc: HTMLElement | Document = document,
  selector = ".checkbox-js",
): void {
  doc.querySelectorAll<HTMLElement>(selector).forEach((rootEl) => {
    const checkbox = new Checkbox(rootEl, {
      id: generateId(rootEl, "checkbox"),
      ids: getPartIds(rootEl, [
        "root",
        "label",
        "control",
        "indicator",
        "hidden-input",
      ]),
      name: getString(rootEl, "name"),
      form: getString(rootEl, "form"),
      defaultChecked: parseCheckedState(rootEl, "defaultChecked"),
      checked: parseCheckedState(rootEl, "checked"),
      disabled: getBoolean(rootEl, "disabled"),
      invalid: getBoolean(rootEl, "invalid"),
      readOnly: getBoolean(rootEl, "readOnly"),
      required: getBoolean(rootEl, "required"),
      value: getString(rootEl, "value"),
      dir: getString<Direction>(rootEl, "dir", ["ltr", "rtl"]),
      onCheckedChange(details) {
        const eventName = getString(rootEl, "onCheckedChange");
        if (eventName) {
          rootEl.dispatchEvent(new CustomEvent(eventName, { detail: details }));
        }
      },
    });
    checkbox.init();
    rootEl.addEventListener("checkbox:set-checked", (event) => {
      const { value } = (event as CustomEvent<{ value: checkbox.CheckedState }>)
        .detail;
      checkbox.api.setChecked(value);
    });
    rootEl.addEventListener("checkbox:toggle-checked", () => {
      checkbox.api.toggleChecked();
    });
    rootEl.addEventListener("checkbox:checked", (event) => {
      const { callback } = (
        event as CustomEvent<{ callback: (value: boolean) => void }>
      ).detail;
      if (typeof callback === "function") callback(checkbox.api.checked);
    });
    rootEl.addEventListener("checkbox:disabled", (event) => {
      const { callback } = (
        event as CustomEvent<{ callback: (value: boolean | undefined) => void }>
      ).detail;
      if (typeof callback === "function") callback(checkbox.api.disabled);
    });
    rootEl.addEventListener("checkbox:indeterminate", (event) => {
      const { callback } = (
        event as CustomEvent<{ callback: (value: boolean) => void }>
      ).detail;
      if (typeof callback === "function") callback(checkbox.api.indeterminate);
    });
    rootEl.addEventListener("checkbox:focused", (event) => {
      const { callback } = (
        event as CustomEvent<{ callback: (value: boolean | undefined) => void }>
      ).detail;
      if (typeof callback === "function") callback(checkbox.api.focused);
    });
    rootEl.addEventListener("checkbox:checked-state", (event) => {
      const { callback } = (
        event as CustomEvent<{
          callback: (value: checkbox.CheckedState) => void;
        }>
      ).detail;
      if (typeof callback === "function") callback(checkbox.api.checkedState);
    });
  });
}
